import {FC, useState} from "react";
import {Box} from "@mui/material";
import {
	ADMIN_USER_RECENT_ACTIVITY_FETCH_PAGE_SIZE,
	PAGE_SIZE_OPTIONS_STANDARD_ADMIN,
	useGetUserRecentActivity,
	type RecentActivityItemDto
} from "@/app2/shared/data";
import { CustomToolbar } from "@/components/admin/bulkEnrollment/CustomToolbar";
import { DataGridPro, GridColDef, GridSelectionModel } from "@mui/x-data-grid-pro";

interface UserRecentActivityTabProps {
	userId: number;
}

/**
 * Admin view of a user's recent activity (watch history)
 *
 * Shows recently completed lessons.
 */
export const UserRecentActivityTab: FC<UserRecentActivityTabProps> = ({userId}) => {
	const {data, isLoading} = useGetUserRecentActivity(userId, {pageSize: ADMIN_USER_RECENT_ACTIVITY_FETCH_PAGE_SIZE});
	const [selectedIds, setSelectedIds] = useState<GridSelectionModel>([]);

	const columns: GridColDef<RecentActivityItemDto>[] = [
		{
			field: "title",
			headerName: "Lesson",
			flex: 1,
			minWidth: 240
		},
		{
			field: "videoDuration",
			headerName: "Duration",
			width: 120,
			valueFormatter: (params) => {
				if (!params.value) return "-";
				const seconds = params.value as number;
				const minutes = Math.floor(seconds / 60);
				const remainingSeconds = seconds % 60;
				return minutes > 0
					? `${minutes}m ${remainingSeconds}s`
					: `${remainingSeconds}s`;
			}
		},
		{
			field: "completedAt",
			headerName: "Watched On",
			flex: 1,
			valueFormatter: (params) => {
				if (!params.value) return "-";
				return new Date(params.value as string).toLocaleDateString();
			}
		},
		{
			field: "course",
			headerName: "Parent Course",
			flex: 1,
			valueGetter: (params) => (params.row.course as any)?.title ?? "-"
		}
	];

	return (
		<Box>
			<DataGridPro
				rows={data?.data ?? []}
				columns={columns}
				loading={isLoading}
				getRowId={(row) => row.id}
				checkboxSelection
				disableSelectionOnClick
				selectionModel={selectedIds}
				onSelectionModelChange={setSelectedIds}
				rowsPerPageOptions={[...PAGE_SIZE_OPTIONS_STANDARD_ADMIN]}
				components={{Toolbar: (): JSX.Element => <CustomToolbar />}}
				initialState={{
					pagination: {pageSize: ADMIN_USER_RECENT_ACTIVITY_FETCH_PAGE_SIZE},
					sorting: {sortModel: [{field: "completedAt", sort: "desc"}]}
				}}
				autoHeight
				sx={{
					"& .MuiDataGrid-row": {cursor: "default"}
				}}
			/>
		</Box>
	);
};

